import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { TabsBar } from '@/common/TabsBar';
import { HeaderWrapper } from '~/styles/layoutElements';
import { BulkActions } from './BulkActions';
import { NotificationFilterDropdown } from './NotificationFilterDropdown';
import { NotificationItemComponent } from './NotificationItem';
import { Pagination } from './Pagination';
import { SortControl } from './SortControl';
import type { NotificationActionFilter } from './types';

interface NotificationsScreenProps {
  isReadView?: boolean;
}

const NOTIFICATIONS_PER_PAGE = 25;

/** The dropdown's "no filter" value; legacy uses the same `0`. */
const ALL = '0';

export function NotificationsScreen({
  isReadView = false,
}: NotificationsScreenProps) {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filterValue, setFilterValue] = useState<string>(ALL);
  const [oldestFirst, setOldestFirst] = useState(false);

  const query = {
    read: isReadView,
    offset: (currentPage - 1) * NOTIFICATIONS_PER_PAGE,
    limit: NOTIFICATIONS_PER_PAGE,
    oldest_first: oldestFirst,
    ...(filterValue !== ALL
      ? { action: filterValue as NotificationActionFilter }
      : {}),
  };

  const { data, isFetching } = api.listNotifications.useQuery({ query });
  const { mutateAsync: markRead } = api.markNotificationsRead.useMutation();
  const { mutateAsync: markUnread } = api.markNotificationsUnread.useMutation();
  const { mutateAsync: deleteNotifications } =
    api.deleteNotifications.useMutation();

  const notifications = data?.data ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / NOTIFICATIONS_PER_PAGE);
  const startIndex = (currentPage - 1) * NOTIFICATIONS_PER_PAGE;
  const endIndex = startIndex + notifications.length;

  const allCurrentSelected =
    notifications.length > 0 &&
    notifications.every((n) => selectedIds.includes(n.id));

  // Both counters move on every action, and the read tab gains whatever the
  // unread tab loses, so refresh the whole family rather than this one page.
  const refresh = async (): Promise<void> => {
    setSelectedIds([]);
    await Promise.all([
      api.listNotifications.invalidateQueries(),
      api.getUnreadNotificationCount.invalidateQueries(),
    ]);
  };

  const handleSelectAll = (): void => {
    if (allCurrentSelected) {
      setSelectedIds(
        selectedIds.filter((id) => !notifications.some((n) => n.id === id)),
      );
    } else {
      const newIds = notifications.map((n) => n.id);
      setSelectedIds([...new Set([...selectedIds, ...newIds])]);
    }
  };

  const handleSelectOne = (id: number): void => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // One code path for the row icons and the Apply button: a row action is a
  // selection of one, which is also why the endpoints take a list.
  const applyToIds = async (action: string, ids: number[]): Promise<void> => {
    if (ids.length === 0) return;
    if (action === 'delete') {
      await deleteNotifications({ body: { ids } });
    } else {
      const toggle = isReadView ? markUnread : markRead;
      await toggle({ body: { ids } });
    }
    await refresh();
  };

  const handlePageChange = (page: number): void => {
    setSelectedIds([]);
    setCurrentPage(page);
  };

  const resetToFirstPage = <T,>(set: (value: T) => void) => {
    return (value: T): void => {
      set(value);
      setSelectedIds([]);
      setCurrentPage(1);
    };
  };

  return (
    <PageContainer>
      <HeaderRow>
        <Title>{t('notifications.notifications')}</Title>
        <RightControls>
          <TabsBar
            tabs={[
              { title: t('wbs.unread'), to: '' },
              { title: t('wbs.read'), to: 'read' },
            ]}
            base="/notifications"
          />
        </RightControls>
        <FilterWrapper>
          <NotificationFilterDropdown
            value={filterValue}
            onChange={resetToFirstPage(setFilterValue)}
          />
        </FilterWrapper>
      </HeaderRow>

      {/* The header bell renders the same rows in its preview, so the list
          needs a handle of its own to assert against. */}
      <NotificationsList data-testid="notifications-list">
        <HeaderBar>
          <BulkActions
            allSelected={allCurrentSelected}
            selectedCount={selectedIds.length}
            isReadView={isReadView}
            onSelectAll={handleSelectAll}
            onApply={(action) => applyToIds(action, selectedIds)}
          />
          <SortControl
            ascending={oldestFirst}
            onToggle={() => resetToFirstPage(setOldestFirst)(!oldestFirst)}
          />
        </HeaderBar>
        {notifications.length === 0 && !isFetching && (
          // Translation Needed
          <EmptyState>No notifications.</EmptyState>
        )}
        {notifications.map((notification) => (
          <NotificationItemComponent
            key={notification.id}
            notification={notification}
            isSelected={selectedIds.includes(notification.id)}
            isReadView={isReadView}
            onToggleSelect={handleSelectOne}
            onToggleRead={(id) => applyToIds('mark-read-unread', [id])}
            onDelete={(id) => applyToIds('delete', [id])}
          />
        ))}
      </NotificationsList>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        startIndex={startIndex}
        endIndex={endIndex}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        // Translation Needed
        itemLabel="notifications"
      />
    </PageContainer>
  );
}

const PageContainer = styled.div`
  box-sizing: border-box;
  font-family: var(--open-sans);
  margin: 0 auto;
  padding-bottom: 60px;
  padding-top: 25px;
  width: 100%;
`;

const HeaderRow = styled(HeaderWrapper)`
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-areas: 'title right filter';
  grid-template-columns: 1fr auto auto;
  margin-bottom: 26px;

  @media (max-width: 799px) {
    grid-template-areas:
      'title right'
      '. filter';
    grid-template-columns: 1fr auto;
  }

  @media (max-width: 511px) {
    grid-template-areas:
      'title right'
      'filter filter';
  }
`;

const RightControls = styled.div`
  grid-area: right;
  justify-self: end;
`;

const FilterWrapper = styled.div`
  grid-area: filter;

  @media (max-width: 511px) {
    > div {
      display: block;
      width: 100%;
    }

    select {
      width: 100%;
    }
  }
`;

const Title = styled.h1`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 32px;
  font-weight: 600;
  grid-area: title;
  letter-spacing: -0.5px;
  margin: 0;
`;

const NotificationsList = styled.div`
  background: var(--white);
  border-radius: 5px;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  overflow: visible;
  position: relative;
`;

const HeaderBar = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--grey-300);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  overflow: visible;
  padding: 17px 20px;
`;

const EmptyState = styled.div`
  color: var(--grey-500);
  font-size: 16px;
  padding: 32px 20px;
  text-align: center;
`;
